import React from "react"

import { InformationCircleIcon } from "@heroicons/react/24/outline"

import type { ConcertPricingMethodDto } from "~/api/dto/concert.dto"
import { useGetConcertPricing } from "~/api/hooks/loan-products/concert/useGetConcertPricing"
import { useGetConcertUserStatus } from "~/api/hooks/useGetConcertUserStatus"
import {
  ConcertAccountStatusBanner,
  ConcertAccountStatusErrorBanner,
  ConcertAccountStatusLoadingBanner,
  getConcertUserStatusFailures,
} from "~/components/Concert/ConcertAccountStatusBanner"
import { CreateConcertUserModal } from "~/components/Concert/CreateConcertUserModal"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import type { ActiveLoan, LoanProduct, QuoteResult } from "~/graphql/client"
import { Dropdown } from "~/ui/lib/options/Select/Dropdown"

import { formatters } from "../Pricing/SystemPricing/utils"

type CreditQuality = `Excellent` | `Good` | `Very Good`

const FLOAT_MATCH_EPSILON = 0.01

const CREDIT_QUALITY_OPTIONS: CreditQuality[] = [
  `Excellent`,
  `Very Good`,
  `Good`,
]

const sortByAprAscending = (
  pricingMethods: ConcertPricingMethodDto[],
): ConcertPricingMethodDto[] => {
  return [...pricingMethods].sort((left, right) => {
    const leftApr = left.apr ?? Number.POSITIVE_INFINITY
    const rightApr = right.apr ?? Number.POSITIVE_INFINITY

    return leftApr - rightApr
  })
}

const mapPricingToCreditQuality = (
  pricingMethods: ConcertPricingMethodDto[],
): Partial<Record<CreditQuality, ConcertPricingMethodDto>> => {
  const sortedPricing = sortByAprAscending(pricingMethods)

  return {
    Excellent: sortedPricing[0],
    [`Very Good`]: sortedPricing[1],
    Good: sortedPricing[2],
  }
}

const formatApr = (apr: number | null): string => {
  if (apr == null) {
    return `-`
  }

  return `${apr.toFixed(2)}%`
}

type ConcertFinancingProps = {
  proposalId: string
  isSaving?: boolean
  onSelectedPricingChange?: (pricing: ConcertPricingMethodDto | null) => void
  hasActiveConcertLoan?: boolean
  proposalQuote?: QuoteResult | null
  selectedLoanProduct?: LoanProduct | null
  activeConcertLoan?: ActiveLoan | null
}

type ConcertUiValues = {
  productType: string
  termYears: number | null
  apr: number | null
  monthlyPayment: number | null
}

const getTermYearsFromLoanProduct = (
  loanProduct: LoanProduct | null | undefined,
): number | null => {
  if (!loanProduct) {
    return null
  }

  return loanProduct.term
}

const getQuoteInitialPayment = (
  quote: QuoteResult | null | undefined,
): number | null => {
  if (!quote || !(`monthlyInitialPayment` in quote)) {
    return null
  }

  return typeof quote.monthlyInitialPayment === `number`
    ? quote.monthlyInitialPayment
    : null
}

const isCloseEnough = (
  firstValue: number | null | undefined,
  secondValue: number | null | undefined,
  epsilon = FLOAT_MATCH_EPSILON,
): boolean => {
  if (firstValue == null || secondValue == null) {
    return false
  }

  return Math.abs(firstValue - secondValue) <= epsilon
}

const getCreditQualityFromExistingQuote = ({
  pricingByCreditQuality,
  proposalQuote,
  selectedLoanProduct,
}: {
  pricingByCreditQuality: Partial<Record<CreditQuality, ConcertPricingMethodDto>>
  proposalQuote: QuoteResult | null | undefined
  selectedLoanProduct: LoanProduct | null | undefined
}): CreditQuality | undefined => {
  const selectedLoanApr = selectedLoanProduct?.rate ?? null
  const selectedLoanTermYears = getTermYearsFromLoanProduct(selectedLoanProduct)

  if (selectedLoanApr != null) {
    const qualityFromApr = CREDIT_QUALITY_OPTIONS.find((creditQuality) => {
      const pricing = pricingByCreditQuality[creditQuality]

      if (pricing?.apr == null) {
        return false
      }

      if (
        selectedLoanTermYears != null &&
        pricing.term != null &&
        pricing.term !== selectedLoanTermYears
      ) {
        return false
      }

      return isCloseEnough(pricing.apr, selectedLoanApr)
    })

    if (qualityFromApr) {
      return qualityFromApr
    }
  }

  const quoteInitialPayment = getQuoteInitialPayment(proposalQuote)
  if (quoteInitialPayment == null) {
    return undefined
  }

  return CREDIT_QUALITY_OPTIONS.find((creditQuality) => {
    const pricing = pricingByCreditQuality[creditQuality]

    return isCloseEnough(
      pricing?.calculations?.introTermPayment,
      quoteInitialPayment,
    )
  })
}

export const ConcertFinancing: React.FC<ConcertFinancingProps> = ({
  proposalId,
  isSaving = false,
  onSelectedPricingChange,
  hasActiveConcertLoan = false,
  proposalQuote,
  selectedLoanProduct,
  activeConcertLoan,
}: ConcertFinancingProps): React.ReactNode => {
  const { local: proposal } = useProposalContext()
  const userStatus = useGetConcertUserStatus(proposalId, {
    enabled: !hasActiveConcertLoan,
  })
  const [showCreateUserModal, setShowCreateUserModal] = React.useState(false)

  const assignee = proposal.mentor ?? proposal.seller
  const assigneeLicense =
    assignee?.licenses?.find((l) => l.state === `CA` && l.name === `CSLB HIS#`)
      ?.identifier ?? null

  const renderUserStatusBanner = (): React.ReactNode => {
    if (hasActiveConcertLoan) {
      return null
    }
    if (userStatus.isError) {
      return <ConcertAccountStatusErrorBanner className="mb-4" />
    }
    if (userStatus.isLoading || userStatus.data == null) {
      return <ConcertAccountStatusLoadingBanner className="mb-4" />
    }
    const failures = getConcertUserStatusFailures(userStatus.data)
    if (failures.length === 0) {
      return null
    }
    return (
      <ConcertAccountStatusBanner
        failures={failures}
        className="mb-4"
        onRequestAccount={() => setShowCreateUserModal(true)}
      />
    )
  }

  const [selectedCreditQuality, setSelectedCreditQuality] =
    React.useState<CreditQuality | null>(null)

  const {
    data: pricingMethods = [],
    isLoading,
    isError,
    error,
  } = useGetConcertPricing(proposalId)

  const pricingByCreditQuality = React.useMemo(
    () => mapPricingToCreditQuality(pricingMethods),
    [pricingMethods],
  )

  const firstAvailableCreditQuality = React.useMemo(() => {
    return CREDIT_QUALITY_OPTIONS.find(
      (quality) => pricingByCreditQuality[quality] !== undefined,
    )
  }, [pricingByCreditQuality])

  const inferredCreditQuality = React.useMemo(() => {
    return getCreditQualityFromExistingQuote({
      pricingByCreditQuality,
      proposalQuote,
      selectedLoanProduct,
    })
  }, [pricingByCreditQuality, proposalQuote, selectedLoanProduct])

  const resolvedCreditQuality = React.useMemo<CreditQuality>(() => {
    if (
      selectedCreditQuality != null &&
      pricingByCreditQuality[selectedCreditQuality] !== undefined
    ) {
      return selectedCreditQuality
    }

    if (
      inferredCreditQuality != null &&
      pricingByCreditQuality[inferredCreditQuality] !== undefined
    ) {
      return inferredCreditQuality
    }

    return firstAvailableCreditQuality ?? `Excellent`
  }, [
    firstAvailableCreditQuality,
    inferredCreditQuality,
    pricingByCreditQuality,
    selectedCreditQuality,
  ])

  const selectedPricing = pricingByCreditQuality[resolvedCreditQuality]

  const creditQualityDropdownOptions = CREDIT_QUALITY_OPTIONS.map(
    (creditQuality) => {
      const hasPricing = pricingByCreditQuality[creditQuality] !== undefined

      return (
        <Dropdown.Item
          key={creditQuality}
          label1={hasPricing ? creditQuality : `${creditQuality} (Unavailable)`}
          onClick={() => {
            if (!hasPricing) {
              return
            }

            setSelectedCreditQuality(creditQuality)
          }}
          isChecked={resolvedCreditQuality === creditQuality}
          showCheck={hasPricing}
        />
      )
    },
  )

  const resolvedUiValues = React.useMemo<ConcertUiValues>(() => {
    if (hasActiveConcertLoan) {
      return {
        productType: selectedLoanProduct?.productName ?? `Propel`,
        termYears:
          getTermYearsFromLoanProduct(selectedLoanProduct) ??
          (activeConcertLoan?.termMonths != null
            ? activeConcertLoan.termMonths / 12
            : null),
        apr: selectedLoanProduct?.rate ?? activeConcertLoan?.rate ?? null,
        monthlyPayment: getQuoteInitialPayment(proposalQuote),
      }
    }

    return {
      productType: selectedPricing?.financingProduct?.name ?? `Propel`,
      termYears: selectedPricing?.term ?? null,
      apr: selectedPricing?.apr ?? null,
      monthlyPayment: selectedPricing?.calculations?.introTermPayment ?? null,
    }
  }, [
    activeConcertLoan?.rate,
    activeConcertLoan?.termMonths,
    hasActiveConcertLoan,
    proposalQuote,
    selectedLoanProduct,
    selectedPricing,
  ])

  React.useEffect(() => {
    onSelectedPricingChange?.(selectedPricing ?? null)
  }, [onSelectedPricingChange, selectedPricing])

  if (!hasActiveConcertLoan && isLoading) {
    return <ConcertFinancingLoading />
  }

  if (!hasActiveConcertLoan && isError) {
    return (
      <div className="rounded-md border border-danger/20 bg-danger/10 px-4 py-3 text-sm text-foreground">
        {error instanceof Error
          ? error.message
          : `Failed to load Concert financing pricing.`}
      </div>
    )
  }

  if (!hasActiveConcertLoan && pricingMethods.length === 0) {
    return (
      <div className="rounded-md border border-warning/20 bg-warning/10 px-4 py-3 text-sm text-foreground">
        No Concert pricing options are available for this proposal.
      </div>
    )
  }

  return (
    <div className="w-full">
      {renderUserStatusBanner()}

      {showCreateUserModal && assignee && (
        <CreateConcertUserModal
          userId={assignee.id}
          name={`${assignee.firstName} ${assignee.lastName}`}
          email={assignee.email}
          phone={assignee.phone}
          license={assigneeLicense}
          onClose={() => setShowCreateUserModal(false)}
        />
      )}

      {!hasActiveConcertLoan && (
        <Dropdown.Container
          disabled={isSaving}
          Label={() => <label>Credit Quality</label>}
          SelectedOption={() => <p>{resolvedCreditQuality}</p>}
          Options={creditQualityDropdownOptions}
        />
      )}

      <div className="mt-5 rounded-xl border border-primary/10 bg-primary/5 px-5 py-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted">
              Product Type
            </p>
            <p className="mt-0.5 text-sm font-semibold text-foreground">
              {resolvedUiValues.productType}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted">
              Term
            </p>
            <p className="mt-0.5 text-sm font-semibold text-foreground">
              {resolvedUiValues.termYears != null
                ? `${resolvedUiValues.termYears} years`
                : `-`}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted">
              APR
            </p>
            <p className="mt-0.5 text-sm font-semibold text-foreground">
              {formatApr(resolvedUiValues.apr)}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted">
              Monthly Payment
            </p>
            <p className="mt-0.5 text-sm font-semibold text-foreground">
              {resolvedUiValues.monthlyPayment != null
                ? formatters.currency(resolvedUiValues.monthlyPayment, 0)
                : `-`}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-start gap-2 rounded-md p-3 text-xs text-muted">
        <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted" />
        <p>
          {hasActiveConcertLoan
            ? `These values reflect the currently active Concert financing on this proposal.`
            : `These are estimated values based on the selected credit quality. Final APR and monthly payment will be confirmed once a credit check has been completed.`}
        </p>
      </div>
    </div>
  )
}

const ConcertFinancingLoading: React.FC = () => {
  return (
    <div role="status" className="mt-2 animate-pulse space-y-4">
      <div className="h-3 w-36 rounded-full bg-border"></div>
      <div className="h-12 rounded-lg border border-border bg-surface"></div>
      <div className="space-y-3 rounded-lg border border-border bg-surface p-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="h-10 rounded-md bg-border"></div>
          <div className="h-10 rounded-md bg-border"></div>
          <div className="h-10 rounded-md bg-border"></div>
          <div className="h-10 rounded-md bg-border"></div>
        </div>
      </div>
      <div className="h-12 rounded-md bg-surface"></div>
      <span className="sr-only">Loading concert financing...</span>
    </div>
  )
}
